import { useEffect, useState, useRef } from "react";
import {
  useTransition,
  animated,
  config as springConfig,
} from "@react-spring/web";

import stylesSheet from "./infoLayer.module.scss";
import { MarkerData } from "Types/markers";
import styled, { useTheme } from "styled-components";
import { device } from "../breakpoints";
import { Link } from "../styles";
import { Box } from "@mui/material";

import { ArticleTeaser } from "../articleLink";
import locales from "locales/locales";

export const InfoLayer = ({
  activeMarker,
  handleClose,
  publisher,
  colorMode,
  name = null,
}: {
  activeMarker: MarkerData | null;
  handleClose: () => void;
  publisher: string;
  colorMode: "light" | "dark";
  name?: string | null;
}) => {
  const [showInfoLayer, setShowInfoLayer] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const infoLayer = useRef(null);
  const wrapper = useRef<null | HTMLDivElement>(null);
  const theme = useTheme();

  const transitions = useTransition(showInfoLayer, {
    config: { ...springConfig.default, velocity: 0, mass: 0.1, tension: 450 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    onRest: () => {
      !showInfoLayer && handleClose();
    },
  });

  useEffect(() => {
    if (activeMarker) {
      setTimeout(() => {
        if (wrapper && wrapper?.current) {
          wrapper.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 0);
      setShowInfoLayer(true);
      setImageLoaded(false);
    } else {
      setShowInfoLayer(false);
    }
  }, [activeMarker]);

  return transitions(
    (styles, item) =>
      item && (
        <Wrapper ref={wrapper}>
          <Container
            ref={infoLayer}
            style={{
              transform: styles.opacity.to(
                (v) => "translate3d(0," + (1 - v) * 100 + "%,0)"
              ),
            }}
          >
            {activeMarker && (
              <MenuContainer>
                <MenuInfos>{name && "La carte des " + name}</MenuInfos>
                <CloseButton
                  onClick={() => {
                    setShowInfoLayer(false);
                  }}
                >
                  <Close />
                </CloseButton>
              </MenuContainer>
            )}

            <div
              className="ui vertical fitted segment info-footer"
              style={{ paddingBottom: 0 }}
            >
              <TextContainer>
                {activeMarker && (
                  <div>
                    <Title>{activeMarker.name}</Title>
                    {/* <Lead>{activeMarker.description}</Lead> */}
                    <Infos>
                      {activeMarker.image && (
                        <ImageContainer>
                          <img
                            src={activeMarker.image}
                            alt={activeMarker.name}
                            style={imageLoaded ? {} : { opacity: 0 }}
                            onLoad={() => setImageLoaded(true)}
                          />
                        </ImageContainer>
                      )}
                      {activeMarker.description && (
                        <Description>{activeMarker.description}</Description>
                      )}
                      <InfoBox>
                        <Box mt={2} p={2}>
                          {activeMarker.date_str && (
                            <InfoItem>
                              <InfoTitle>Dates</InfoTitle>
                              <div>{activeMarker.date_str}</div>
                            </InfoItem>
                          )}
                          {activeMarker.address && (
                            <InfoItem>
                              <InfoTitle>Lieu</InfoTitle>
                              <div>{activeMarker.address}</div>
                            </InfoItem>
                          )}
                          {activeMarker.website && (
                            <InfoItem>
                              <InfoTitle>Site Web</InfoTitle>
                              <div>
                                <a
                                  href={activeMarker.website}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {activeMarker.website.replace("https://", "")}
                                </a>
                              </div>
                            </InfoItem>
                          )}
                        </Box>
                      </InfoBox>
                    </Infos>

                    {activeMarker.storyIds &&
                      activeMarker.storyIds.length > 0 && (
                        <TeaserContainer>
                          <ArticleTeaser
                            title="À ce sujet"
                            color={
                              publisher === "tdg"
                                ? colorMode === "dark"
                                  ? "#5A64A8"
                                  : "#2c3890"
                                : colorMode === "dark"
                                ? "#A34746"
                                : "#d9010e"
                            }
                            pub={publisher}
                            ids={activeMarker.storyIds}
                          />
                        </TeaserContainer>
                      )}
                  </div>
                )}
              </TextContainer>
            </div>

            <TextContainerBorder>
              <TextContainer>
                <div className={stylesSheet["filters-group"]}>
                  <div className={stylesSheet["filters-group-title"]}>
                    {activeMarker?.style_list &&
                      activeMarker?.style_list.map((text, index) => (
                        <InlineFilter key={`filter_${index}`}>
                          {text}
                        </InlineFilter>
                      ))}
                  </div>
                </div>
              </TextContainer>
            </TextContainerBorder>

            <InfoFooter>
              <InfoFooterContent>
                <Link href="mailto:interactif@tamedia.ch">
                  {locales.readerUpdate[theme.lang as "fr" | "de"]}
                </Link>
              </InfoFooterContent>
            </InfoFooter>
          </Container>
          <Mask
            style={{
              opacity: styles.opacity,
            }}
          />
        </Wrapper>
      )
  );
};

const Mask = styled(animated.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;
`;

const InlineFilter = styled.div`
  display: inline-block;
  padding: 0.45em 1.5rem;
  background: ${(props) => props.theme.greyColor};
  color: ${(props) => props.theme.textColor};
  font-family: var(--font-sans-serif);
  border-radius: 5px;
  margin-right: 1rem;
  margin-bottom: 1rem;
  font-size: 1.5rem;

  &:last-child {
    margin-right: 0;
  }
`;

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 20px 20px 0px 0px;
  @media ${device.tablet} {
    width: 50%;
  }
`;
const Container = styled(animated.div)`
  z-index: 999;
  position: sticky;
  transform: translate3d(0, 0, 0);
  left: 0px;
  width: 100%;
  overflow-y: scroll;
  background-color: ${(props) => props.theme.plainColor};
  color: ${(props) => props.theme.textColor};
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.085);

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    top: 10vh;
    height: 90vh;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    top: 0;
    height: 100vh;
  }

  overscroll-behavior-y: none;
  scroll-behavior: smooth;

  .text {
    color: ${(props) => props.theme.textColor};
  }

  .spacing-top {
    margin-top: 0.5em;
  }

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`;

const Infos = styled.div`
  margin-top: 1rem;
  font-size: 1.5rem;
  line-height: 1.5;
  color: ${(props) => props.theme.textColor};

  img {
    max-width: 100%;
    margin: auto;
  }
`;

const InfoFooter = styled.div`
  height: 10vh;
  display: flex;
  align-items: center;
`;

const InfoFooterContent = styled.div`
  width: 600px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const InfoTitle = styled.div`
  font-weight: 800;
  margin-top: 0.5rem;
`;

const InfoItem = styled.div`
  margin: 1rem 0;
`;

const CloseButton = styled.div`
  background-color: var(--text-color);
  margin-left: auto;
  height: 60px;
  width: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 10vh;
  position: absolute;
  right: 0;
  top: 0;

  &:after {
    content: "";
    margin-top: 75%;
  }
`;

const Close = styled.div`
  position: relative;
  display: inline-block;
  width: 32px;
  height: 32px;
  opacity: 0.75;

  &:before,
  &:after {
    left: 15px;
    position: absolute;
    content: " ";
    height: 33px;
    width: 2px;
    background-color: ${(props) =>
      props.theme.colorMode === "dark" ? "#000000" : "#ffffff"};
  }
  &:before {
    transform: rotate(45deg);
  }
  &:after {
    transform: rotate(-45deg);
  }
`;

const MenuContainer = styled(animated.div)`
  height: 60px;
  font-weight: 100;
  user-select: none;
  background-color: ${(props) => props.theme.plainColor};
  border-bottom: 1px solid ${(props) => props.theme.borderColor};
  position: sticky;
  transform: translate3d(0, 0, 0);
  top: 0;
  z-index: 2;
  display: flex;
  justify-content: space-around;
  align-items: stretch;
`;

const MenuInfos = styled.div`
  color: ${(props) => props.theme.mainColor};
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  line-height: 1.5;
  padding-right: 2rem;
  width: calc(100% - 20vh -1em);
  float: left;
  height: 100%;
  cursor: pointer;
  font-weight: bold;
  font-size: 1.5rem;
  max-width: 600px;
  padding: 0 2rem;
  margin: auto;
  width: 100%;
`;

const Title = styled.div`
  font-family: var(--font-source);
  font-weight: 600;
  line-height: 1;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-size: 3em;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-size: 4em;
  }
`;

const Lead = styled.div`
  font-family: var(--font-sans-serif);
  line-height: 1.35;
  margin-top: 1.25rem;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-size: 1.85em;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-size: 1.85em;
  }
`;

const Meta = styled.div`
  font-family: var(--font-sans-serif);
  line-height: 1.35;
  margin-top: 1em;
  margin-bottom: 2em;
  opacity: 0.55;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-size: 1.3em;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-size: 1.3em;
  }
`;

const Description = styled.div`
  position: relative;
  font-family: var(--font-serif);
  margin: 2rem 0;
  margin-bottom: 0;
  font-size: 1.15em;
  line-height: 1.65;
`;

const TextContainerBorder = styled.div``;

const TextContainer = styled.div`
  max-width: 600px;
  margin: auto;
  padding: 0 2rem;
`;
const InfoBox = styled.div`
  background-color: ${(props) => props.theme.greyColor};
  font-family: var(--font-sans-serif);
  margin: 2rem 0;
`;

const ImageContainer = styled.div`
  @keyframes placeholderShimmer {
    0% {
      background-position: -1200px 0;
    }
    100% {
      background-position: 1200px 0;
    }
  }
  height: 100%;
  width: 100%;
  aspect-ratio: 2/1;
  overflow: hidden;
  background-color: #ffffff;
  background-image: linear-gradient(
    to right,
    rgba(0, 0, 0, 0.08) 0%,
    rgba(0, 0, 0, 0.15) 15%,
    rgba(0, 0, 0, 0.08) 30%
  );
  background-size: 1200px 100%;
  animation: placeholderShimmer 2s linear;
  animation-iteration-count: infinite;
  img {
    margin-bottom: -10px;
  }
`;

const TeaserContainer = styled.div`
  max-width: 400px;
  margin: 1em auto 0 auto;
`;
